import React from 'react';
import Modal from './Modal';
import { ShieldCheck, FileText, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export default function ContractModal({ open }) {
  const { acceptTerms, logout } = useAuth();
  const [loading, setLoading] = React.useState(false);

  const handleAccept = async () => {
    try {
      setLoading(true);
      // Timeout de 8s para evitar travamento infinito se o Firestore estiver offline ou lento
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error("Tempo limite excedido (Firestore offline/sem resposta)")), 8000)
      );
      await Promise.race([acceptTerms(), timeoutPromise]);
      toast.success('Termos aceitos com sucesso! Bem-vindo(a) à plataforma.');
    } catch (e) {
      console.warn("Erro ao aceitar os termos:", e);
      toast.error(`Erro ao aceitar os termos: ${e.message || 'Verifique sua conexão.'}`);
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={() => {}} hideCloseButton={true}>
      <div style={{ textAlign: 'center', padding: '10px 0' }}>
        <div style={{ 
          width: '64px', height: '64px', borderRadius: '50%', 
          background: 'rgba(116, 242, 255, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px auto', color: 'var(--accent)'
        }}>
          <ShieldCheck size={32} />
        </div>
        
        <h2 style={{ fontSize: '1.4rem', color: 'var(--text-primary)', marginBottom: '10px' }}>
          Termos de Uso e Política de Dados
        </h2>
        
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '20px', lineHeight: '1.5' }}>
          Para garantir a segurança jurídica e transparência da nossa plataforma de Inteligência de Mercado, solicitamos o aceite dos nossos termos de prestação de serviços.
        </p>

        <div style={{ 
          background: 'rgba(0,0,0,0.2)', padding: '15px', borderRadius: '8px', 
          textAlign: 'left', fontSize: '0.85rem', color: 'var(--text-muted)',
          maxHeight: '300px', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.05)',
          marginBottom: '25px', display: 'flex', flexDirection: 'column', gap: '15px'
        }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <FileText size={16} color="var(--accent)" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong style={{ color: 'var(--text-primary)' }}>1. Natureza e Origem dos Dados (Adequação à LGPD)</strong>
              <p style={{ margin: '4px 0 0 0', lineHeight: '1.4' }}>A plataforma atua unicamente como um motor de busca avançado. Os leads disponibilizados são estritamente contatos corporativos (B2B) e de profissionais liberais, extraídos em tempo real de fontes públicas (ex: Google Maps). O tratamento destes dados possui finalidade legítima de apoio comercial, amparado pelo Art. 7º, IX da Lei Geral de Proteção de Dados (LGPD).</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <FileText size={16} color="var(--accent)" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong style={{ color: 'var(--text-primary)' }}>2. Responsabilidade pelas Abordagens (Anti-SPAM)</strong>
              <p style={{ margin: '4px 0 0 0', lineHeight: '1.4' }}>O contratante é o único responsável pelo uso dos dados extraídos. A LeadsZone não se responsabiliza por bloqueios de contas de WhatsApp, e-mail ou redes sociais. O contratante compromete-se a adotar boas práticas de prospecção, não enviar SPAM e sempre disponibilizar opção de descadastro (opt-out).</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <FileText size={16} color="var(--accent)" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong style={{ color: 'var(--text-primary)' }}>3. Exatidão dos Dados e Limitação Tecnológica ("As-Is")</strong>
              <p style={{ margin: '4px 0 0 0', lineHeight: '1.4' }}>Os dados são fornecidos "no estado em que se encontram" nas fontes públicas, sem garantia de exatidão ou taxa de conversão comercial. Por depender de serviços de terceiros (APIs de mapas), a plataforma isenta-se de responsabilidade por instabilidades operadas por tais provedores.</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <FileText size={16} color="var(--accent)" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong style={{ color: 'var(--text-primary)' }}>4. Pagamentos, Créditos e Reembolso</strong>
              <p style={{ margin: '4px 0 0 0', lineHeight: '1.4' }}>A comercialização ocorre via pacotes de créditos consumidos sob demanda. O direito de arrependimento (Art. 49 do CDC) aplica-se apenas aos créditos <strong>não utilizados</strong> em 7 dias. Não haverá reembolso proporcional para créditos já consumidos na geração de listas.</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <FileText size={16} color="var(--accent)" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong style={{ color: 'var(--text-primary)' }}>5. Foro e Legislação</strong>
              <p style={{ margin: '4px 0 0 0', lineHeight: '1.4' }}>A plataforma opera sob a legislação brasileira. Quaisquer litígios serão resolvidos no foro da comarca da sede da empresa operadora da LeadsZone.</p>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button 
            onClick={handleAccept}
            disabled={loading}
            style={{
              background: 'linear-gradient(135deg, var(--accent) 0%, #00b4d8 100%)',
              color: '#000', border: 'none', padding: '14px', borderRadius: '8px',
              fontWeight: 700, fontSize: '1rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}
          >
            {loading ? 'Processando...' : (
              <>
                <CheckCircle2 size={18} /> Li e aceito os termos do contrato
              </>
            )}
          </button>
          
          <button 
            onClick={logout}
            disabled={loading}
            style={{
              background: 'transparent', color: 'var(--text-secondary)',
              border: 'none', padding: '10px', fontSize: '0.9rem', cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Recusar e sair
          </button>
        </div>
      </div>
    </Modal>
  );
}
